import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { ArrowsUpDownIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";

import DeleteAllRefsButton from "./DeleteAllRefsButton";
import DebouncedInput from "../DebouncedInput";
import { SearchIcon } from "../../Icons/Icons";
import api from "../../utilities/api";

const columnHelper = createColumnHelper();

// Same order as the mapping CSV of Tools → Import Standards (ofid, standard, entry, tag),
// plus the record's field name
const columns = [
  columnHelper.accessor("ofid", { header: "OFID", size: 140 }),
  columnHelper.accessor("fieldName", {
    header: "Field Name",
    cell: (info) => (
      <span className="[overflow-wrap:anywhere]">{info.getValue()}</span>
    ),
    size: 220,
  }),
  columnHelper.accessor("standard", { header: "Standard", size: 180 }),
  columnHelper.accessor("entry", {
    header: "Entry",
    cell: (info) => (
      <span className="[overflow-wrap:anywhere]">{info.getValue()}</span>
    ),
    minSize: 300,
  }),
  columnHelper.accessor("tag", { header: "Tag", size: 220 }),
];

const STATUSES = [
  { value: "record", label: "Unchanged" },
  { value: "draftAdded", label: "Added in draft" },
  { value: "draftRemoved", label: "Removed in draft" },
];
const ALL_STATUSES = STATUSES.map((status) => status.value);

const rowClass = (status, index) => {
  if (status === "draftAdded") return "bg-yellow-200";
  if (status === "draftRemoved") return "bg-yellow-200 line-through text-gray-500";
  return index % 2 === 0 ? "bg-slate-100" : "bg-slate-300";
};

// Multi-select filter dropdown, styled like the Versions filter of the Library
const FilterDropdown = ({ label, options, selected, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Click-away listener
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const isActive = selected.length !== options.length;
  const toggle = (value) =>
    onChange(
      selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value],
    );

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 text-sm font-medium border rounded-lg transition-all duration-200 bg-white text-gray-700 border-gray-300 hover:border-cyan-500 hover:text-cyan-700"
      >
        {isActive ? `${label} (${selected.length})` : label}
        <ChevronDownIcon
          className={`w-4 h-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="absolute left-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
          {/* Select All / Clear All buttons */}
          <div className="flex gap-2 px-4 py-2 border-b border-gray-200">
            <button
              onClick={() => onChange(options.map((option) => option.value))}
              className="text-xs text-cyan-700 hover:text-cyan-900 font-medium transition-colors"
            >
              Select All
            </button>
            <span className="text-gray-300">|</span>
            <button
              onClick={() => onChange([])}
              className="text-xs text-gray-500 hover:text-red-600 transition-colors"
            >
              Clear All
            </button>
          </div>
          <div className="py-2 max-h-60 overflow-y-auto">
            {options.map((option) => (
              <label
                key={option.value}
                className="flex items-center gap-3 px-4 py-2 cursor-pointer hover:bg-gray-50 transition-colors"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(option.value)}
                  onChange={() => toggle(option.value)}
                  className="w-4 h-4 text-cyan-700 border-gray-300 rounded focus:ring-cyan-500 focus:ring-2 accent-cyan-700"
                />
                <span className="text-sm text-gray-700">
                  {option.label}{" "}
                  <span className="text-gray-400 text-xs whitespace-nowrap">
                    ({option.count})
                  </span>
                </span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

FilterDropdown.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      count: PropTypes.number.isRequired,
    }),
  ).isRequired,
  selected: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
};

/**
 * Admin → Stand.-Refs: read-only list of all standard references of the
 * records. Changes in open drafts are highlighted yellow: references a draft
 * adds or changes as extra rows, references it removes struck through.
 */
const StandardRefTable = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sorting, setSorting] = useState([{ id: "ofid", desc: false }]);
  const [selectedStandards, setSelectedStandards] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState(ALL_STATUSES);
  const [search, setSearch] = useState("");

  const fetchRefs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/standards/refs`);
      setData(response.data);
    } catch (error) {
      console.error("Error fetching standard references:", error);
      toast.error("Failed to load standard references");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRefs();
  }, [fetchRefs]);

  // Standards that occur in the references, with their reference counts
  const standardOptions = useMemo(() => {
    const counts = new Map();
    for (const row of data) counts.set(row.standard, (counts.get(row.standard) || 0) + 1);
    return [...counts.keys()]
      .sort()
      .map((name) => ({ value: name, label: name, count: counts.get(name) }));
  }, [data]);

  const statusOptions = useMemo(
    () =>
      STATUSES.map((status) => ({
        ...status,
        count: data.filter((row) => row.status === status.value).length,
      })),
    [data],
  );

  // All standards selected after (re)loading
  useEffect(() => {
    setSelectedStandards(standardOptions.map((option) => option.value));
  }, [standardOptions]);

  const resetFilters = () => {
    setSearch("");
    setSelectedStandards(standardOptions.map((option) => option.value));
    setSelectedStatuses(ALL_STATUSES);
  };

  const filteredData = useMemo(() => {
    const term = search.trim().toLowerCase();
    return data.filter(
      (row) =>
        selectedStandards.includes(row.standard) &&
        selectedStatuses.includes(row.status) &&
        (!term ||
          row.ofid.toLowerCase().includes(term) ||
          row.fieldName.toLowerCase().includes(term) ||
          row.entry.toLowerCase().includes(term) ||
          row.tag.toLowerCase().includes(term)),
    );
  }, [data, selectedStandards, selectedStatuses, search]);

  const draftRowCount = data.length - statusOptions[0].count;

  const table = useReactTable({
    data: filteredData,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: { pageSize: 25 },
    },
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        Loading standard references...
      </div>
    );
  }

  return (
    <div className="p-2 text-black fill-gray-600">
      {/* Search, filters and actions — same style as the Library */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-1">
          <SearchIcon />
          <DebouncedInput
            value={search}
            onChange={setSearch}
            debounce={300}
            className="bg-transparent outline-none border-b-2 w-48 focus:w-64 duration-300 border-gray-600"
            placeholder="Search ..."
          />
        </div>
        <div className="flex items-center gap-4">
          <FilterDropdown
            label="Standards"
            options={standardOptions}
            selected={selectedStandards}
            onChange={setSelectedStandards}
          />
          <FilterDropdown
            label="Status"
            options={statusOptions}
            selected={selectedStatuses}
            onChange={setSelectedStatuses}
          />
          {/* Reset all filters: clear search, select all standards and statuses */}
          <button
            onClick={resetFilters}
            className="text-xs text-gray-500 hover:text-red-600 transition-colors"
          >
            Reset
          </button>
        </div>
        <span className="text-sm text-gray-600">
          {filteredData.length} of {data.length} references
        </span>
        <DeleteAllRefsButton
          onDeleted={fetchRefs}
          className="ml-auto py-1 px-3 text-center font-normal text-sm border-2 rounded-lg transition duration-300 text-red-700 border-red-300 bg-white hover:bg-red-50 hover:border-red-500 disabled:opacity-50"
        />
      </div>
      {draftRowCount > 0 && (
        <p className="mt-2 text-sm text-gray-600">
          <span className="inline-block w-3 h-3 mr-1 align-middle bg-yellow-200 border border-yellow-400" />
          Changes in open drafts: added or changed references, and{" "}
          <span className="line-through">removed</span> ones ({draftRowCount}).
        </p>
      )}

      <div className="mt-2 flex flex-col">
        <div className="overflow-x-auto">
          <div className="inline-block min-w-full py-2 align-middle">
            <table className="min-w-full text-left">
              <thead className="bg-cyan-900 text-white">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        style={{
                          width: header.column.columnDef.size,
                          minWidth: header.column.columnDef.minSize,
                        }}
                        className="py-2 px-2 whitespace-nowrap"
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                        {header.column.getCanSort() && (
                          <button
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            <ArrowsUpDownIcon
                              className="h-6 w-6 text-white relative"
                              style={{ top: "6px" }}
                            />
                          </button>
                        )}
                        {
                          {
                            asc: "🔼",
                            desc: "🔽",
                          }[header.column.getIsSorted()]
                        }
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.length ? (
                  table.getRowModel().rows.map((row, i) => (
                    <tr
                      key={row.id}
                      className={`text-sm ${rowClass(row.original.status, i)}`}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="py-1 px-2 align-top">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr className="text-center h-32">
                    <td colSpan={columns.length}>No standard references found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-end mt-2 gap-2">
        <button
          onClick={() => table.firstPage()}
          disabled={!table.getCanPreviousPage()}
          className="p-1 border border-gray-300 px-2 disabled:opacity-30"
        >
          {"<<"}
        </button>
        <button
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
          className="p-1 border border-gray-300 px-2 disabled:opacity-30"
        >
          {"<"}
        </button>
        <button
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
          className="p-1 border border-gray-300 px-2 disabled:opacity-30"
        >
          {">"}
        </button>
        <button
          onClick={() => table.lastPage()}
          disabled={!table.getCanNextPage()}
          className="p-1 border border-gray-300 px-2 disabled:opacity-30"
        >
          {">>"}
        </button>
        <span className="flex items-center gap-1">
          <input
            type="number"
            value={table.getState().pagination.pageIndex + 1}
            onChange={(e) => {
              const page = e.target.value ? Number(e.target.value) - 1 : 0;
              table.setPageIndex(page);
            }}
            className="border text-right rounded w-14 bg-transparent"
          />{" "}
          of {table.getPageCount()}
        </span>
        <select
          value={table.getState().pagination.pageSize}
          onChange={(e) => table.setPageSize(Number(e.target.value))}
          className="p-2 bg-transparent"
        >
          {[25, 50, 100].map((pageSize) => (
            <option key={pageSize} value={pageSize}>
              Show {pageSize}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default StandardRefTable;
